/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './CommentsPanel';

const CommentsPanelManager: ComponentManagerFn<'CommentsPanel'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'CommentsPanel'>(platform, 'CommentsPanel'),
    isChildAllowed: (page, item, childType) =>
      (
        [
          'CommentActionMenuItems',
          'TriggerOnComment',
          'TriggerOnMention',
        ] as ItemType[]
      ).includes(childType),
    type: 'CommentsPanel',
    name: 'Comments Panel',
    defaultValue,
    props: {
      placeholder: {
        label: 'Placeholder',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          'A placeholder that will be displayed in the comment box before the user starts typing the comments.',
      },
      context: {
        label: 'Context',
        type: 'string',
        placeholder: 'e.g. InvoiceComments',
        helpText:
          'A unique context across your application. e.g. OrderComments, InvoiceComments etc.',
      },
      contextValue: {
        label: 'Context Value',
        type: 'expression',
        helpText: `A value within the above context to seperate out the comments.

You can provide a static text, e.g. "All", if you don't want to seperate the comments.

To have different comments per record or record type, you can provide an expression e.g. \`\${$.alias.invoiceId}\` or \`\${$.alias.invoiceType}\``,
        returnType: async () => 'string',
      },
      panelWidth: {
        label: 'Width',
        type: 'number',
        min: 740,
        max: 1200,
        step: 40,
      },
      enableRemoveAlways: {
        label: 'Enable Remove Always',
        type: 'boolean',
        helpText:
          "Enable this to enable remove action always. By default it'll be enabled for 5min.",
      },
    },
    getDisplayText: (item) => item.props.context,
    category: 'Data',
    canHaveChildren: () => false,
    noOverlay: true,
    layout: 'auto',
    icon: { iconName: 'comments' },
    description: `**Overview**<br/>
The Comments Panel is a robust collaboration widget that embeds a fully functional comment thread directly into your page layout.
Unlike the standard Comments component (which hides discussions behind a slide-out drawer), the Comments Panel is designed to be always visible. This makes it the ideal choice for workflows where discussion is the primary task—such as Ticket Management systems, Design Reviews, or Audit Logs—where users need to see the conversation history immediately upon loading the page.

**1. Context & Data Binding**<br/>
To attach the discussion to a specific record, you must configure the "Context." This tells the system which thread to load.
* **Context:** A static string defining the category of the discussion (e.g., "TicketReviews", "ProjectUpdates").
* **Context Value:** An expression defining the specific ID of the record (e.g., \`ticket_\${$.ticketStore.id}\`).
* **Requirement:** This must resolve to a String. If your ID is a number, wrap it in a template string or convert it.
* **Behavior:** As the Context Value changes (e.g., user selects a different ticket), the panel automatically refreshes to show the conversation for that specific record.

**2. Layout & Control**<br/>
Since this component sits inside your page structure (rather than floating over it), it offers specific layout controls.
* **Panel Width:** Sets the fixed width of the comment area (Range: 740px – 1200px). This ensures that the text remains readable and the layout predictable, even on ultra-wide monitors.
* **Enable Remove Always:**
    * **Default (Off):** Users can only delete their own comments within a short time window (typically 5-15 minutes) after posting.
    * **Enabled (On):** Users (or moderators, depending on permissions) can delete comments at any time. This is useful for admin panels or long-running threads where cleanup is necessary.

**3. Extensibility**<br/>
The panel supports the same powerful logic extensions as the drawer version.
* **Supported Children:**
    * **TriggerOnComment:** Execute logic (e.g., "Send Email" or "Update Status") immediately after a user posts a comment.
    * **TriggerOnMention:** Execute logic when a user mentions a colleague (e.g., @john.doe).
    * **Comment Action Menu Items:** Inject custom actions (like "Mark as Resolved" or "Convert to Task") into the "..." menu of individual comments.

**4. Usage Guide: Panel vs. Drawer**<br/>
* **Visibility:** Drawer is hidden by default (opens on click); Panel is always visible (embedded in layout).
* **Screen Real Estate:** Drawer is minimal (just an icon); Panel is moderate (occupies a column/section).
* **Best For:** Drawer is for secondary discussions ("Notes"); Panel is for primary workflows ("Reviews").`,
  };
};

export default CommentsPanelManager;
